import { useCallback } from 'react';
import { useApolloClient } from '@apollo/react-hooks';
// eslint-disable-next-line jira/restricted/graphql-tag
import gqlTagPolaris from 'graphql-tag';
import { type ApolloQueryResult, NetworkStatus } from 'apollo-client';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';
import type { Ari } from '@atlassian/jira-platform-ari/src/index.tsx';
import type { PolarisApolloClient } from '@atlassian/jira-polaris-lib-remote-context/src/controllers/providers/types.tsx';
import {
	fireOperationalAnalytics,
	useAnalyticsEvents,
	type Attributes,
} from '@atlassian/jira-product-analytics-bridge';
import { wrapPromiseWithFetchInteraction } from '@atlassian/jira-polaris-lib-analytics/src/common/utils/interaction/index.tsx';
import type { ProjectConfigFetcher } from '../types.tsx';
import { createAnalyticsEventPayload } from '../utils.tsx';
import type { GetPolarisProjectQuery, ProjectTypeGQL } from './types.tsx';
import { getEarlyFetchResponse } from './early-scripts/index.tsx';

/**
 * GraphQL fragment for view data structure
 * Contains the common view fields used in project config queries
 *
 * ❗❗❗ If making changes here, ensure to update the corresponding ./early-scripts/constants.tsx
 */
export const ViewFragment = gqlTagPolaris`
  fragment EdgeView on PolarisView {
    __typename
    id
    xid
    name
    emoji
    description @skip(if: $skipDescription)
    uuid @include(if: $isShareViewDialogEnabled)
    containsArchived
    immutable
    visualizationType
    lastCommentsViewedTimestamp
    sortMode
    hideEmptyGroups
    hideEmptyColumns
    groupBy {
        __typename
        key: jiraFieldKey
    }
    verticalGroupBy {
        __typename
        key: jiraFieldKey
    }
    groupValues {
        ...EdgeGroupValue
    }
    verticalGroupValues {
        ...EdgeGroupValue
    }
    sort {
        ...EdgeSorted
    }
    fields {
        __typename
        key: jiraFieldKey
    }
    hidden {
        __typename
        key: jiraFieldKey
    }
    filter {
        ...EdgeFilter
    }
    tableColumnSizes {
        ...EdgeColumnSizes
    }
    fieldRollups {
        ...EdgeFieldRollups
    }
    matrixConfig {
        __typename
        axes {
            __typename
            dimension
            field {
                __typename
                key: jiraFieldKey
            }
            fieldOptions {
                ...EdgeGroupValue
            }
            reversed
        }
    }
    timelineConfig {
        __typename
        summaryCardField {
            __typename
            key: jiraFieldKey
        }
        startDateField {
            __typename
            key: jiraFieldKey
        }
        dueDateField {
            __typename
            key: jiraFieldKey
        }
        mode
        startTimestamp
        endTimestamp
    }
    rank
    viewSetId
    enabledAutoSave
    layoutType
    createdAt
    updatedAt
  }


  fragment EdgeColumnSizes on PolarisViewTableColumnSize {
      __typename
      field {
          __typename
          key: jiraFieldKey
      }
      size
  }

  fragment EdgeFieldRollups on PolarisViewFieldRollup {
      __typename
      field {
          __typename
          key: jiraFieldKey
      }
      rollup
  }

  fragment EdgeSorted on PolarisSortField {
      __typename
      order
      field {
          __typename
          key: jiraFieldKey
      }
    }

  fragment EdgeFilter on PolarisViewFilter {
      __typename
      type: kind
      field {
          __typename
          key: jiraFieldKey
      }
      values {
          ...EdgeFilterValue
      }
  }

  fragment EdgeFilterValue on PolarisViewFilterValue {
      __typename
      stringValue
      numericValue
      enumValue
      operator
  }

  fragment EdgeGroupValue on PolarisGroupValue {
      __typename
      label
      id
  }
`;

/**
 * GraphQL query for fetching project configuration data
 * This is the main query used to fetch project details, viewsets, and other configuration
 *
 * ❗❗❗ If making changes here, ensure to update the corresponding ./early-scripts/constants.tsx
 */
const PROJECT_CONFIG_QUERY = gqlTagPolaris`
  query jira_polaris_ProjectQuery_reduced(
    $ari: ID!,
    $skipIdeaTypes: Boolean!,
    $isShareViewDialogEnabled: Boolean!,
    $skipDescription: Boolean!,
  ) {
    polarisProject(id: $ari) {
      ...EdgeProjectConfigReducedGQL
    }
  }

  fragment EdgeProjectConfigReducedGQL on PolarisProject {
      __typename
      id
      rankField
      onboarded
      selectedDeliveryProject
      viewsets {
        __typename
        id
        name
        type
        views {
          ...EdgeView
        }
        viewsets {
          __typename
          id
          name
          type
          rank
          views {
            ...EdgeView
          }
        }
      }
      ideaTypes @skip(if: $skipIdeaTypes) {
        __typename
        id
        name
        iconUrl
      }
      onboardTemplate
      onboardedAt
  }
  ${ViewFragment}
`;

/**
 * Helper function to perform the actual Apollo query
 */
const performApolloQuery = async (
	apolloClient: PolarisApolloClient,
	variables: {
		ari: Ari;
		skipIdeaTypes: boolean;
		isShareViewDialogEnabled: boolean;
		skipDescription: boolean;
	},
	noCache?: boolean,
): Promise<ApolloQueryResult<GetPolarisProjectQuery>> => {
	return apolloClient.query<GetPolarisProjectQuery>({
		query: PROJECT_CONFIG_QUERY,
		variables,
		fetchPolicy: noCache ? 'network-only' : 'cache-first',
	});
};

export const fetchProject = async (
	apolloClient: PolarisApolloClient,
	createAnalyticsEvent: CreateUIAnalyticsEvent,
	projectAri: Ari,
	noCache?: boolean,
	fireAnalyticsEvent?: boolean,
): Promise<ProjectTypeGQL> => {
	const fetchStart = new Date();

	// Query parameters
	const queryParams = {
		ari: projectAri,
		skipIdeaTypes: true,
		isShareViewDialogEnabled: true,
		skipDescription: true,
	};

	let result: ApolloQueryResult<GetPolarisProjectQuery>;

	const earlyResponsePromise = getEarlyFetchResponse(queryParams);
	if (earlyResponsePromise) {
		try {
			// Use the early fetched data
			const data = await wrapPromiseWithFetchInteraction(
				earlyResponsePromise,
				'earlyScript/jira_polaris_ProjectQuery_reduced',
			);

			// Create a result object matching the Apollo result structure
			result = {
				data,
				loading: false,
				networkStatus: NetworkStatus.ready,
				stale: false,
			};

			// Write the early fetched data to Apollo cache
			apolloClient.writeQuery({
				query: PROJECT_CONFIG_QUERY,
				variables: queryParams,
				data,
			});
		} catch (error) {
			// Fall back to normal Apollo query on error
			result = await performApolloQuery(apolloClient, queryParams, noCache);
		}
	} else {
		// No early fetch data, use normal Apollo query
		result = await performApolloQuery(apolloClient, queryParams, noCache);
	}

	if (result.errors !== undefined) {
		throw new Error(`project-config.fetch-error:${result.errors.map((e) => e.message).join(', ')}`);
	}

	if (fireAnalyticsEvent === true) {
		const analyticsData: Attributes = createAnalyticsEventPayload({
			projectAri,
			durationMs: new Date().getTime() - fetchStart.getTime(),
			data: result.data,
		});

		fireOperationalAnalytics(
			createAnalyticsEvent({}),
			'JpdFetchProjectConfig succeeded',
			analyticsData,
		);
	}

	if (!result.data?.polarisProject) {
		throw new Error('project-config.fetch-error: no data or no data node');
	}

	return result.data.polarisProject;
};

export const useProjectFetcher = (): [ProjectConfigFetcher] => {
	const client = useApolloClient();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const fetcher = useCallback(
		async (ari: Ari, noCache?: boolean, fireAnalyticsEvent?: boolean): Promise<ProjectTypeGQL> =>
			fetchProject(client, createAnalyticsEvent, ari, noCache, fireAnalyticsEvent),
		[client, createAnalyticsEvent],
	);

	return [fetcher];
};
